import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Box,
  Stack
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatDate, formatCurrency } from '../../utils/salesReportHelpers';

/**
 * Row component with expandable product details
 */
function SalesReportRow({ order, index }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {index + 1}
        </TableCell>
        <TableCell>{order.orderId}</TableCell>
        <TableCell>{order.customerName}</TableCell>
        <TableCell>{order.category || 'N/A'}</TableCell>
        <TableCell>{order.type || 'N/A'}</TableCell>
        <TableCell>{formatDate(order.deliveredDate)}</TableCell>
        <TableCell align="right">{formatCurrency(order.totalAmount)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Products
              </Typography>
              <Table size="small" aria-label="products">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.products && order.products.map((product, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {product.name_english || product.name || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {product.category_english || product.category || 'N/A'}
                      </TableCell>
                      <TableCell align="right">{product.quantity || 0}</TableCell>
                      <TableCell align="right">{formatCurrency(product.price || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(product.subtotal || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/**
 * Main Sales Report Table Component
 */
export default function SalesReportTable({ data }) {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No data available for the selected filters
        </Typography>
      </Box>
    );
  }

  // Calculate totals
  const totalOrders = data.length;
  const grandTotal = data.reduce((sum, order) => sum + order.totalAmount, 0);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2, px: 2 }}>
        <Typography variant="h6">
          Total Orders: <strong>{totalOrders}</strong>
        </Typography>
        <Typography variant="h6">
          Grand Total: <strong>{formatCurrency(grandTotal)}</strong>
        </Typography>
      </Stack>
      
      <TableContainer component={Paper}>
        <Table aria-label="sales report table">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell />
              <TableCell><strong>#</strong></TableCell>
              <TableCell><strong>Order ID</strong></TableCell>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Delivered Date</strong></TableCell>
              <TableCell align="right"><strong>Total Amount</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((order, index) => (
              <SalesReportRow key={order.orderId || index} order={order} index={index} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

